import { formatDate } from 'date-fns';

import ChevronRight from '@simplywallst/ui-core/icons/ChevronRightSmallSolid';
import { Button } from '@simplywallst/ui-core';
import { toCurrencyTruncated } from '@/utilities/formatting';
import Tooltip from '@/components/Tooltip';

import type { CurrencyISO } from '@/constants/currencies';

import { PrimaryNarrativeIcon } from './PrimaryNarrativeIcon';

type Props = {
  currencyISO: CurrencyISO;
  defaultNarrative: {
    id: string;
    title: string;
    updatedAt: string | null;
    valuation: {
      id: string;
      fairValue: number | null;
      type: string;
    };
    owner: {
      displayName: string | null;
      imageUrl: string | null;
    };
  } | null;
  isOnboardingNarrative?: boolean;
  selectedDate: string | null;
  selectedFairValue: number | null;
};

export const DefaultState = ({
  currencyISO,
  defaultNarrative,
  isOnboardingNarrative = false,
  selectedDate,
  selectedFairValue,
}: Props) => {
  if (!defaultNarrative) {
    return null;
  }

  const { title, updatedAt, owner, valuation } = defaultNarrative;
  const { fairValue, type } = valuation;

  const targetFairValue = selectedFairValue ?? fairValue;
  const targetDate = selectedDate ?? updatedAt;

  return (
    <div className="grid h-auto gap-x1 sm:h-x12">
      <div className="grid grid-cols-[auto_auto] items-center justify-start gap-x1">
        <div className="grid grid-cols-[24px_max-content] items-center gap-x2 opacity-80">
          <PrimaryNarrativeIcon type={type} owner={owner} />
          <div>
            <span className="m-0 text-xs text-soft">
              {toCurrencyTruncated(
                targetFairValue || 0,
                2,
                currencyISO ?? 'XXX'
              )}
            </span>
            <span className="sr-only">author fair value</span>
          </div>
        </div>
        {targetDate !== null && (
          <div>
            <span className="text-tiny text-softer">
              {formatDate(new Date(targetDate), 'dd MMM')}
            </span>
            <span className="sr-only">author updated this narrative</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-[auto_auto] items-start justify-between gap-x2">
        <h4 className="m-0 line-clamp-2 text-xs text-softer sm:line-clamp-3">
          {title}
        </h4>
        <Tooltip
          placement="top-end"
          light
          title={
            <p
              data-theme="light"
              className="m-0 line-clamp-2 w-48 text-tiny text-solid"
            >
              You can select a narrative from the community or create one
              yourself.
            </p>
          }
          initialOpen={isOnboardingNarrative}
        >
          <Button styleType="gentle" alignIcon="right">
            Select
            <ChevronRight />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
